import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  ButtonV2,
  CheckBoxFieldv2,
  LabraToolTipV2,
  NoteColors,
  NoteV2,
  RadioButtonGroupMapping,
  RadioButtonsV2,
  TextArea,
  TextInputV2,
} from '@labrav/react-components'
import React, { useEffect, useState } from 'react'
import { faExternalLinkAlt } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './FlyOutProductRedirectionSettings.styles'
import {
  ApplicationAccessMethod,
  FlyoutProductSections,
} from '../../../types/addEditProduct'
import { FormikHandlers, FormikProps, useFormikContext } from 'formik'
import { Typography } from '@mui/material'
import { FormLockedType } from '../../../types/FormLockedType'
import {
  applicationURLText,
  customApplicationText,
  customInstructionsText,
  getApplicationAccessMethodOptions,
  getPublicFacingSignupPageAvailableButtonOptions,
  isPublicFacingSignupPageAvailableCustomInstruction,
  nonPublicFacingOptionalText,
  publicFacingOptionalText,
} from '../../../common/utils/productCreationUtil'
import { FlyoutProductRedirectionSettingsFaqs } from './FlyoutProductRedirectionSettingsFaqs'

type FlyOutProductRedirectionSettingsProps = {
  detailView?: boolean
  detailViewTooltip?: string
  handleRefLinkClick?: (inex: number) => void
}

export const FlyOutProductRedirectionSettings: React.FC<
  FormLockedType & FlyOutProductRedirectionSettingsProps
> = ({ isFormLocked, detailView, detailViewTooltip, handleRefLinkClick }) => {
  const { classes } = useStyles()
  const {
    values,
    errors,
    touched,
    submitCount,
    handleBlur,
    handleChange,
    setFieldValue,
  } = useFormikContext() as FormikProps<FlyoutProductSections>

  const infoCollectionFormMetaData = values.infoCollectionFormMetaData
  const infoCollectionFormMetaDataErrors = errors.infoCollectionFormMetaData
  const infoCollectionFormMetaDataTouched = touched.infoCollectionFormMetaData

  const applicationAccessMethodOptions: RadioButtonGroupMapping[] =
    getApplicationAccessMethodOptions(isFormLocked || detailView)

  const isPublicFacingSignupPageAvailableButtonOptions: RadioButtonGroupMapping[] =
    getPublicFacingSignupPageAvailableButtonOptions(isFormLocked || detailView)

  const handleIsPublicFacingSignupPageAvailableChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value as string
    setFieldValue(
      'infoCollectionFormMetaData.redirectionSettings.isPublicFacingSignupPageAvailable',
      value === 'Yes' ? true : false
    )
  }

  const handleApplicationAccessMethodChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = (event.target as HTMLInputElement).value as string
    const previousValue =
      infoCollectionFormMetaData?.redirectionSettings?.applicationAccessMethod
    setFieldValue(
      'infoCollectionFormMetaData.redirectionSettings.applicationAccessMethod',
      value
    )
    if (
      (previousValue === ApplicationAccessMethod.VIDEO ||
        previousValue === ApplicationAccessMethod.DOCUMENT) &&
      (value === ApplicationAccessMethod.VIDEO ||
        value === ApplicationAccessMethod.DOCUMENT) &&
      previousValue !== value
    ) {
      setFieldValue(
        'infoCollectionFormMetaData.redirectionSettings.sendDemoToBuyer',
        false
      )
    }
  }

  const handleCheckboxChange =
    (handleChange: FormikHandlers['handleChange']) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange({
        target: {
          name: 'infoCollectionFormMetaData.redirectionSettings.sendDemoToBuyer',
          value: e.target.checked,
        },
      })
    }

  useEffect(() => {
    if (
      !infoCollectionFormMetaData?.redirectionSettings
        ?.isPublicFacingSignupPageAvailable &&
      !infoCollectionFormMetaData?.redirectionSettings?.customInstructions
    ) {
      setFieldValue(
        'infoCollectionFormMetaData.redirectionSettings.customInstructions',
        'Our team will reach out to you shortly to begin the onboarding process.'
      )
    }
    if (
      !infoCollectionFormMetaData?.redirectionSettings?.applicationAccessMethod
    ) {
      setFieldValue(
        'infoCollectionFormMetaData.redirectionSettings.applicationAccessMethod',
        ApplicationAccessMethod.TESTING_CREDENTIALS
      )
    }
  }, [infoCollectionFormMetaData?.redirectionSettings?.applicationAccessMethod])

  const applicationLoginPageURL = (
    <div
      className={classes.sectionContainer}
      data-testid="product-redirection-settings-login-page-url"
    >
      <div className={classes.textContainer}>
        <div className={classes.sectionHeadingContainer}>
          <Typography className={classes.sectionHeading}>
            Application login page URL
          </Typography>
          {!infoCollectionFormMetaData?.redirectionSettings
            ?.isPublicFacingSignupPageAvailable &&
            infoCollectionFormMetaData?.redirectionSettings
              ?.applicationAccessMethod !==
              ApplicationAccessMethod.TESTING_CREDENTIALS && (
              <Typography className={classes.sectionText}>Optional</Typography>
            )}
        </div>

        <Typography className={classes.sectionText}>
          {infoCollectionFormMetaData?.redirectionSettings
            ?.isPublicFacingSignupPageAvailable
            ? customApplicationText
            : applicationURLText}
        </Typography>
      </div>
      <LabraToolTipV2
        followCursor={detailView}
        tooltipPlacement="bottom-start"
        description={detailViewTooltip}
        className={classes.fullWidth}
      >
        <div className={classes.textWidth}>
          <TextInputV2
            keyName="infoCollectionFormMetaData.redirectionSettings.applicationLoginPageUrl"
            title="Application login page URL"
            type="text"
            placeHolder=""
            value={
              infoCollectionFormMetaData?.redirectionSettings
                ?.applicationLoginPageUrl
            }
            updatedValue={
              infoCollectionFormMetaData?.redirectionSettings
                ?.applicationLoginPageUrl
            }
            maxNumber={72}
            isError={
              !!(
                infoCollectionFormMetaDataErrors?.redirectionSettings
                  ?.applicationLoginPageUrl &&
                infoCollectionFormMetaDataTouched?.redirectionSettings
                  ?.applicationLoginPageUrl
              ) ||
              (!!infoCollectionFormMetaDataErrors?.redirectionSettings
                ?.applicationLoginPageUrl &&
                submitCount > 0)
            }
            onChange={handleChange}
            isReadOnly={isFormLocked as boolean}
            isRequired={false}
            errorText={
              (!!infoCollectionFormMetaDataTouched?.redirectionSettings
                ?.applicationLoginPageUrl &&
                infoCollectionFormMetaDataErrors?.redirectionSettings
                  ?.applicationLoginPageUrl) ||
              submitCount > 0
                ? infoCollectionFormMetaDataErrors?.redirectionSettings
                    ?.applicationLoginPageUrl
                : ''
            }
            showSpinner={false}
            onBlur={handleBlur}
            viewOnly={detailView}
          />
        </div>
      </LabraToolTipV2>
    </div>
  )

  const optionalText = infoCollectionFormMetaData?.redirectionSettings
    ?.isPublicFacingSignupPageAvailable
    ? publicFacingOptionalText
    : nonPublicFacingOptionalText

  const customInstructions = (
    <div
      className={classes.sectionContainer}
      data-testid="product-redirection-settings-custom-instructions"
    >
      <div className={classes.textContainer}>
        <div className={classes.sectionHeadingContainer}>
          <Typography className={classes.sectionHeading}>
            Custom instructions
          </Typography>
          {(infoCollectionFormMetaData?.redirectionSettings
            ?.isPublicFacingSignupPageAvailable ||
            infoCollectionFormMetaData?.redirectionSettings
              ?.applicationAccessMethod !==
              ApplicationAccessMethod.TESTING_CREDENTIALS) && (
            <Typography className={classes.sectionText}>Optional</Typography>
          )}
        </div>
        <Typography className={classes.sectionText}>
          {infoCollectionFormMetaData?.redirectionSettings
            ?.isPublicFacingSignupPageAvailable
            ? isPublicFacingSignupPageAvailableCustomInstruction
            : customInstructionsText}
          <ButtonV2
            styleType="link"
            onClick={() => handleRefLinkClick && handleRefLinkClick(3)}
          >
            (Refer no. 9 in image no. 4)
          </ButtonV2>
        </Typography>
      </div>
      <LabraToolTipV2
        followCursor={detailView}
        tooltipPlacement="bottom-start"
        description={detailViewTooltip}
        className={classes.fullWidth}
      >
        <TextArea
          keyName="infoCollectionFormMetaData.redirectionSettings.customInstructions"
          title={optionalText}
          rows={10}
          height={100}
          maxCharacters={1000}
          type="string"
          placeHolder=""
          value={
            infoCollectionFormMetaData?.redirectionSettings?.customInstructions
          }
          isError={
            !!(
              infoCollectionFormMetaDataTouched?.redirectionSettings
                ?.customInstructions &&
              infoCollectionFormMetaDataErrors?.redirectionSettings
                ?.customInstructions
            )
          }
          onChange={handleChange}
          isReadOnly={isFormLocked as boolean}
          isRequired={false}
          errorText={
            (!!infoCollectionFormMetaDataTouched?.redirectionSettings
              ?.customInstructions &&
              infoCollectionFormMetaDataErrors?.redirectionSettings
                ?.customInstructions) ||
            ''
          }
          showSpinner={false}
          onBlur={handleBlur}
          viewOnly={detailView}
          updatedValue={
            infoCollectionFormMetaData?.redirectionSettings?.customInstructions
          }
        />
      </LabraToolTipV2>
    </div>
  )
  const [indexModal, setIndexModal] = useState<number>(0)

  const applicationText =
    infoCollectionFormMetaData?.redirectionSettings?.applicationAccessMethod ===
    ApplicationAccessMethod.TESTING_CREDENTIALS
      ? "In this section you can specify how and when buyers can access your application since they cannot sign up without your team's involvement."
      : 'In this section you can specify how and when buyers can access your application since they cannot sign up independently.'
  return (
    <BoxV2
      heading={'Redirection settings'}
      headingIcon={
        <FontAwesomeIcon
          icon={faExternalLinkAlt}
          style={{ width: '18px', height: '14px' }}
        />
      }
      secondaryActions={
        <FlyoutProductRedirectionSettingsFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <div
        className={classes.sectionContainer}
        data-testid="product-redirection-settings-public-facing-radio-button"
      >
        <Typography className={classes.sectionHeading}>
          Do you have a public facing login page where buyers can register
          themselves to your application?
        </Typography>
        <LabraToolTipV2
          followCursor={detailView}
          tooltipPlacement="bottom-start"
          description={detailViewTooltip}
        >
          <RadioButtonsV2
            name={
              'infoCollectionFormMetaData.redirectionSettings.isPublicFacingSignupPageAvailable'
            }
            checkedValue={{
              key: infoCollectionFormMetaData?.redirectionSettings
                ?.isPublicFacingSignupPageAvailable
                ? 'Yes'
                : 'No',
              value: infoCollectionFormMetaData?.redirectionSettings
                ?.isPublicFacingSignupPageAvailable
                ? 'Yes'
                : 'No',
            }}
            handleChange={handleIsPublicFacingSignupPageAvailableChange}
            radioButtonMappings={isPublicFacingSignupPageAvailableButtonOptions}
          />
        </LabraToolTipV2>
      </div>

      {infoCollectionFormMetaData?.redirectionSettings
        ?.isPublicFacingSignupPageAvailable && (
        <div className={classes.sectionContainer}>
          {applicationLoginPageURL}
          {customInstructions}
        </div>
      )}

      {!infoCollectionFormMetaData?.redirectionSettings
        ?.isPublicFacingSignupPageAvailable && (
        <>
          <div className={classes.sectionContainer}>
            <div className={classes.textContainer}>
              <Typography className={classes.sectionHeading}>
                Application access
              </Typography>
              <Typography className={classes.sectionText}>
                {applicationText}
              </Typography>
            </div>
            <NoteV2
              description={
                "Following information is also needed by AWS team in conducting User Acceptance Testing (UAT) to approve this product's listing on AWS Marketplace. During UAT, the AWS team simulates the entire purchase flow as a buyer, from initial purchase to accessing your application."
              }
              backgroundColor={NoteColors.YELLOW}
            ></NoteV2>
            <LabraToolTipV2
              followCursor={detailView}
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
            >
              <div
                className={classes.gridContainer}
                data-testid="product-redirection-settings-application-access-method-radio-button"
              >
                {applicationAccessMethodOptions.map(option => (
                  <div key={option.key} className={classes.gridItem}>
                    <RadioButtonsV2
                      name={
                        'infoCollectionFormMetaData.redirectionSettings.applicationAccessMethod'
                      }
                      checkedValue={{
                        key:
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.applicationAccessMethod ||
                          ApplicationAccessMethod.TESTING_CREDENTIALS,
                        value:
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.applicationAccessMethod ||
                          ApplicationAccessMethod.TESTING_CREDENTIALS,
                      }}
                      handleChange={handleApplicationAccessMethodChange}
                      radioButtonMappings={[option]}
                      border={true}
                    />
                  </div>
                ))}
              </div>
            </LabraToolTipV2>
          </div>

          {infoCollectionFormMetaData?.redirectionSettings
            ?.applicationAccessMethod ===
            ApplicationAccessMethod.TESTING_CREDENTIALS && (
            <>
              {applicationLoginPageURL}
              <div className={classes.sectionContainer}>
                <div className={classes.textContainer}>
                  <Typography className={classes.sectionHeading}>
                    Testing credentials
                  </Typography>
                  <Typography className={classes.sectionText}>
                    Please provide a testing email and password which AWS team
                    can use to login and test your listing completely.{' '}
                    <ButtonV2
                      styleType="link"
                      onClick={() =>
                        handleRefLinkClick && handleRefLinkClick(3)
                      }
                    >
                      (Refer no. 10 in image no. 4)
                    </ButtonV2>
                  </Typography>
                </div>
                <LabraToolTipV2
                  followCursor={detailView}
                  tooltipPlacement="bottom-start"
                  description={detailViewTooltip}
                >
                  <div className={classes.inputColumn}>
                    <div
                      className={classes.textWidth}
                      data-testid="product-redirection-settings-testing-creds-email"
                    >
                      <TextInputV2
                        keyName="infoCollectionFormMetaData.redirectionSettings.testingCredentials.email"
                        title="Test account email"
                        type="email"
                        placeHolder=""
                        value={
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.testingCredentials?.email || ''
                        }
                        maxNumber={72}
                        isError={
                          !!(
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.testingCredentials
                              ?.email &&
                            infoCollectionFormMetaDataTouched
                              ?.redirectionSettings?.testingCredentials?.email
                          ) ||
                          (!!infoCollectionFormMetaDataErrors
                            ?.redirectionSettings?.testingCredentials?.email &&
                            submitCount > 0)
                        }
                        onChange={handleChange}
                        isReadOnly={isFormLocked as boolean}
                        isRequired={false}
                        errorText={
                          (!!infoCollectionFormMetaDataTouched
                            ?.redirectionSettings?.testingCredentials?.email &&
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.testingCredentials
                              ?.email) ||
                          (submitCount > 0
                            ? infoCollectionFormMetaDataErrors
                                ?.redirectionSettings?.testingCredentials?.email
                            : '')
                        }
                        showSpinner={false}
                        viewOnly={detailView}
                        onBlur={handleBlur}
                        updatedValue={
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.testingCredentials?.email
                        }
                      />
                    </div>
                    <div
                      className={classes.textWidth}
                      data-testid="product-redirection-settings-testing-creds-password"
                    >
                      <TextInputV2
                        keyName="infoCollectionFormMetaData.redirectionSettings.testingCredentials.password"
                        title="Test account password"
                        type="text"
                        placeHolder=""
                        value={
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.testingCredentials?.password || ''
                        }
                        maxNumber={72}
                        isError={
                          !!(
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.testingCredentials
                              ?.password &&
                            infoCollectionFormMetaDataTouched
                              ?.redirectionSettings?.testingCredentials
                              ?.password
                          ) ||
                          (!!infoCollectionFormMetaDataErrors
                            ?.redirectionSettings?.testingCredentials
                            ?.password &&
                            submitCount > 0)
                        }
                        onChange={handleChange}
                        isReadOnly={isFormLocked as boolean}
                        isRequired={false}
                        errorText={
                          (!!infoCollectionFormMetaDataTouched
                            ?.redirectionSettings?.testingCredentials
                            ?.password &&
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.testingCredentials
                              ?.password) ||
                          (submitCount > 0
                            ? infoCollectionFormMetaDataErrors
                                ?.redirectionSettings?.testingCredentials
                                ?.password
                            : '')
                        }
                        showSpinner={false}
                        viewOnly={detailView}
                        onBlur={handleBlur}
                        updatedValue={
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.testingCredentials?.password
                        }
                      />
                    </div>
                  </div>
                </LabraToolTipV2>
                <NoteV2
                  description={
                    'These Testing Credentials will only be shared with Labra and AWS Marketplace team. Failure to provide these may result in delayed approval of listing or, in some cases, rejection by the AWS team. You can delete the testing account once your product is live. Failure to provide these credentials may result in delayed approval of your listing or, in some cases, rejection by the AWS team. You can remove the testing account once your product is live.'
                  }
                  backgroundColor={NoteColors.YELLOW}
                ></NoteV2>
              </div>
              {customInstructions}
            </>
          )}

          {infoCollectionFormMetaData?.redirectionSettings
            ?.applicationAccessMethod ===
            ApplicationAccessMethod.INSTRUCTIONS && (
            <>
              <div
                className={classes.sectionContainer}
                data-testid="product-redirection-settings-custom-instructions"
              >
                <div className={classes.textContainer}>
                  <div className={classes.sectionHeadingContainer}>
                    <Typography className={classes.sectionHeading}>
                      Instructions
                    </Typography>
                  </div>
                  <Typography className={classes.sectionText}>
                    Please provide exact instructions on how buyers can gain
                    access to your application. These instructions will be
                    shared to the buyer in the subscription confirmation email
                    sent after they submit the buyer registration page.{' '}
                    <ButtonV2
                      styleType="link"
                      onClick={() =>
                        handleRefLinkClick && handleRefLinkClick(3)
                      }
                    >
                      (Refer no. 9 in image no. 4)
                    </ButtonV2>
                  </Typography>
                </div>
                <LabraToolTipV2
                  followCursor={detailView}
                  tooltipPlacement="bottom-start"
                  description={detailViewTooltip}
                  className={classes.fullWidth}
                >
                  <TextArea
                    keyName="infoCollectionFormMetaData.redirectionSettings.customInstructions"
                    title="Custom instructions"
                    rows={10}
                    maxCharacters={1000}
                    height={100}
                    type="string"
                    placeHolder=""
                    value={
                      infoCollectionFormMetaData?.redirectionSettings
                        ?.customInstructions
                    }
                    updatedValue={
                      infoCollectionFormMetaData?.redirectionSettings
                        ?.customInstructions
                    }
                    isError={
                      !!(
                        infoCollectionFormMetaDataTouched?.redirectionSettings
                          ?.customInstructions &&
                        infoCollectionFormMetaDataErrors?.redirectionSettings
                          ?.customInstructions
                      )
                    }
                    viewOnly={detailView}
                    onChange={handleChange}
                    isReadOnly={isFormLocked as boolean}
                    isRequired={false}
                    errorText={
                      (!!infoCollectionFormMetaDataTouched?.redirectionSettings
                        ?.customInstructions &&
                        infoCollectionFormMetaDataErrors?.redirectionSettings
                          ?.customInstructions) ||
                      ''
                    }
                    showSpinner={false}
                    onBlur={handleBlur}
                  />
                </LabraToolTipV2>
                <NoteV2
                  description={
                    'Failure to provide these instructions may result in delayed publication of your listing or, in some cases, rejection by the AWS team.'
                  }
                  backgroundColor={NoteColors.YELLOW}
                ></NoteV2>
              </div>
              {applicationLoginPageURL}
            </>
          )}

          {infoCollectionFormMetaData?.redirectionSettings
            ?.applicationAccessMethod === ApplicationAccessMethod.VIDEO && (
            <>
              <div
                className={classes.sectionContainer}
                data-testid="product-redirection-settings-video-section"
              >
                <div
                  className={classes.sectionContainer}
                  data-testid="product-redirection-settings-demo-product-url"
                >
                  <div className={classes.textContainer} data-testid>
                    <div className={classes.sectionHeadingContainer}>
                      <Typography className={classes.sectionHeading}>
                        Demo product video URL
                      </Typography>
                    </div>

                    <Typography className={classes.sectionText}>
                      Provide a link to your product demo video which AWS team
                      can view to test your listing. The product video must be
                      externally hosted and publicly available.{' '}
                      <ButtonV2
                        styleType="link"
                        onClick={() =>
                          handleRefLinkClick && handleRefLinkClick(3)
                        }
                      >
                        (Refer no. 11 in image no. 4)
                      </ButtonV2>
                    </Typography>
                  </div>
                  <LabraToolTipV2
                    followCursor={detailView}
                    tooltipPlacement="bottom-start"
                    description={detailViewTooltip}
                    className={classes.fullWidth}
                  >
                    <div className={classes.textWidth}>
                      <TextInputV2
                        keyName="infoCollectionFormMetaData.redirectionSettings.demoVideoUrl"
                        title="Enter product video URL"
                        type="text"
                        placeHolder=""
                        value={
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.demoVideoUrl || ''
                        }
                        updatedValue={
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.demoVideoUrl || ''
                        }
                        maxNumber={72}
                        isError={
                          !!(
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.demoVideoUrl &&
                            infoCollectionFormMetaDataTouched
                              ?.redirectionSettings?.demoVideoUrl
                          ) ||
                          !!(
                            submitCount > 0 &&
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.demoVideoUrl
                          )
                        }
                        onChange={handleChange}
                        isReadOnly={isFormLocked as boolean}
                        isRequired={false}
                        errorText={
                          (!!infoCollectionFormMetaDataTouched
                            ?.redirectionSettings?.demoVideoUrl &&
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.demoVideoUrl) ||
                          (submitCount > 0
                            ? infoCollectionFormMetaDataErrors
                                ?.redirectionSettings?.demoVideoUrl
                            : '')
                        }
                        showSpinner={false}
                        onBlur={handleBlur}
                        viewOnly={detailView}
                      />
                    </div>
                  </LabraToolTipV2>
                </div>
                <LabraToolTipV2
                  followCursor={detailView}
                  tooltipPlacement="bottom-start"
                  description={detailViewTooltip}
                >
                  <div
                    className={classes.checkBoxContainer}
                    data-testid="product-redirection-settings-send-demo-to-buyer-checkbox"
                  >
                    <CheckBoxFieldv2
                      keyName="infoCollectionFormMetaData.redirectionSettings.sendDemoToBuyer"
                      title="Share this demo video with your buyers in the subscription confirmation email sent once they submit the buyer registration pagee form."
                      value={
                        infoCollectionFormMetaData?.redirectionSettings
                          ?.sendDemoToBuyer || false
                      }
                      onChangeBox={handleCheckboxChange(handleChange)}
                      disabled={isFormLocked || detailView}
                    />
                  </div>
                </LabraToolTipV2>
                {applicationLoginPageURL}
                {customInstructions}
              </div>
            </>
          )}

          {infoCollectionFormMetaData?.redirectionSettings
            ?.applicationAccessMethod === ApplicationAccessMethod.DOCUMENT && (
            <>
              <div
                className={classes.sectionContainer}
                data-testid="product-redirection-settings-document-section"
              >
                <div
                  className={classes.sectionContainer}
                  data-testid="product-redirection-settings-onboarding-document-url"
                >
                  <div className={classes.textContainer}>
                    <div className={classes.sectionHeadingContainer}>
                      <Typography className={classes.sectionHeading}>
                        Onboarding document URL
                      </Typography>
                    </div>

                    <Typography className={classes.sectionText}>
                      Please provide a link to your document which your buyers
                      can use to understand the onboarding process and your
                      application. The document must be externally hosted and
                      publicly accessible.{' '}
                      <ButtonV2
                        styleType="link"
                        onClick={() =>
                          handleRefLinkClick && handleRefLinkClick(3)
                        }
                      >
                        (Refer no. 11 in image no. 4)
                      </ButtonV2>
                    </Typography>
                  </div>
                  <LabraToolTipV2
                    followCursor={detailView}
                    tooltipPlacement="bottom-start"
                    description={detailViewTooltip}
                    className={classes.fullWidth}
                  >
                    <div className={classes.textWidth}>
                      <TextInputV2
                        keyName="infoCollectionFormMetaData.redirectionSettings.demoDocumentUrl"
                        title="Enter onboarding document URL"
                        type="text"
                        placeHolder=""
                        value={
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.demoDocumentUrl || ''
                        }
                        updatedValue={
                          infoCollectionFormMetaData?.redirectionSettings
                            ?.demoDocumentUrl || ''
                        }
                        maxNumber={72}
                        isError={
                          !!(
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.demoDocumentUrl &&
                            infoCollectionFormMetaDataTouched
                              ?.redirectionSettings?.demoDocumentUrl
                          ) ||
                          !!(
                            submitCount > 0 &&
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.demoDocumentUrl
                          )
                        }
                        onChange={handleChange}
                        isReadOnly={isFormLocked as boolean}
                        isRequired={false}
                        errorText={
                          (!!infoCollectionFormMetaDataTouched
                            ?.redirectionSettings?.demoDocumentUrl &&
                            infoCollectionFormMetaDataErrors
                              ?.redirectionSettings?.demoDocumentUrl) ||
                          (submitCount > 0
                            ? infoCollectionFormMetaDataErrors
                                ?.redirectionSettings?.demoDocumentUrl
                            : '')
                        }
                        showSpinner={false}
                        onBlur={handleBlur}
                        viewOnly={detailView}
                      />
                    </div>
                  </LabraToolTipV2>
                </div>
                <LabraToolTipV2
                  followCursor={detailView}
                  tooltipPlacement="bottom-start"
                  description={detailViewTooltip}
                >
                  <div
                    className={classes.checkBoxContainer}
                    data-testid="product-redirection-settings-send-demo-to-buyer-checkbox"
                  >
                    <CheckBoxFieldv2
                      keyName="infoCollectionFormMetaData.redirectionSettings.sendDemoToBuyer"
                      title="Share this onboarding document with your buyers in the subscription confirmation email sent once they submit the buyer registration page form."
                      value={
                        infoCollectionFormMetaData?.redirectionSettings
                          ?.sendDemoToBuyer
                      }
                      onChangeBox={handleCheckboxChange(handleChange)}
                      disabled={isFormLocked || detailView}
                    />
                  </div>
                </LabraToolTipV2>
                {applicationLoginPageURL}
                {customInstructions}
              </div>
            </>
          )}
        </>
      )}
    </BoxV2>
  )
}
